import React, { memo, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import StyledTable from 'app/components/StyledTable';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import TextCell from 'app/pages/TicketRefund/components/TextCell';
import { Title } from 'app/pages/TicketRefund/components/Typo';
import { formatNumber, getFromDateToDate } from 'utils/helper';
import { renderTicketDetail } from '../../../TicketSelectedList';
import useScrollIntoElement from 'utils/hooks/useScrollIntoElement';

interface CancellationListProps {
  cancellationListData: any;
  isPolicyOrder: boolean;
}

const CancellationList = (props: CancellationListProps) => {
  const { cancellationListData, isPolicyOrder } = props;

  const translations = useSelector(selectTranslations);

  const anchorPoint = useRef<HTMLDivElement>(null);
  const { onScrollIntoElement } = useScrollIntoElement({
    elementRef: anchorPoint,
  });

  const columns = useMemo(() => {
    let columns = [
      {
        title: (
          <span style={{ paddingLeft: 8 }}>
            {translations?.['b2b.txt.ticketName'] ?? ''}
          </span>
        ),
        dataIndex: 'ticketName',
        key: 'ticketName',
        width: 125,
      },
      {
        title: translations?.['b2b.txt.ticketPark'] ?? '',
        dataIndex: 'ticketPark',
        key: 'ticketPark',
        width: 200,
      },
      {
        title: translations?.['b2b.txt.ticketType'] ?? '',
        dataIndex: 'ticketType',
        key: 'ticketType',
        width: 100,
      },
      {
        title: translations?.['b2b.txt.ticket-usage-from'] ?? '',
        dataIndex: 'usageDate',
        key: 'usageDate',
        width: 116,
      },
      {
        title: translations?.['b2b.txt.ticket-usage-to'] ?? '',
        dataIndex: 'ticketExpired',
        key: 'ticketExpired',
        width: 116,
      },
      {
        title: translations?.['b2b.txt.ticket-detail'] ?? '',
        dataIndex: 'ticketDetail',
        key: 'ticketDetail',
        width: 103,
      },
      {
        title: (
          <span style={{ display: 'block', textAlign: 'right' }}>
            {translations?.['b2b.txt.ticketAmount'] ?? ''}
          </span>
        ),
        dataIndex: 'ticketAmount',
        key: 'ticketAmount',
        width: 100,
      },
      {
        title: (
          <span style={{ display: 'block', textAlign: 'right' }}>
            {translations?.['b2b.txt.unitPrice'] ?? ''}
          </span>
        ),
        dataIndex: 'unitPrice',
        key: 'unitPrice',
        width: 110,
      },
      {
        title: (
          <span style={{ display: 'block', textAlign: 'right' }}>
            {translations?.['b2b.txt.totalAmount'] ?? ''}
          </span>
        ),
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        width: 120,
      },
    ];
    if (isPolicyOrder)
      columns = columns.concat([
        {
          title: (
            <span style={{ textAlign: 'right', display: 'block' }}>
              {translations?.['b2b.txt.penaltyAccordingToPolicy'] ?? ''}
            </span>
          ),
          dataIndex: 'penaltyPolicy',
          key: 'penaltyPolicy',
          width: 130,
        },
        {
          title: (
            <span style={{ textAlign: 'right', display: 'block' }}>
              {translations?.['b2b.txt.fine-amount'] ?? ''}
            </span>
          ),
          dataIndex: 'fineAmount',
          key: 'fineAmount',
          width: 110,
        },
      ]);
    return columns;
  }, [isPolicyOrder, translations]);

  const totalTicketAmount = useRef(0);

  const dataSource = useMemo(() => {
    totalTicketAmount.current = 0;
    return cancellationListData?.map((ticket, idx) => {
      totalTicketAmount.current =
        totalTicketAmount.current + (ticket?.amount ?? 0);

      const { fromDate, toDate } = getFromDateToDate({
        catalog: { ...ticket?.catalog },
        date: { ...ticket?.dateFrom },
        show: { ...ticket?.show },
        product: { ...ticket?.product },
        ticket: { ...ticket },
      });

      const rowData: Record<string, any> = {
        key: idx,
        ticketName: (
          <TextCell style={{ paddingLeft: 8 }}>
            {ticket?.product?.name}
          </TextCell>
        ),
        ticketPark: <TextCell>{ticket?.park?.name}</TextCell>,
        ticketType: <TextCell>{ticket?.catalog?.name}</TextCell>,
        usageDate: <TextCell>{fromDate}</TextCell>,
        ticketExpired: <TextCell>{toDate}</TextCell>,
        ticketDetail: (
          <TextCell>
            {renderTicketDetail([
              ticket?.product?.restaurant?.name,
              ticket?.ZoneName,
              ticket?.PerformanceTime,
            ])}
          </TextCell>
        ),
        ticketAmount: (
          <TextCell style={{ textAlign: 'right' }}>{ticket?.amount}</TextCell>
        ),
        unitPrice: (
          <TextCell style={{ textAlign: 'right' }}>{`${formatNumber(
            ticket?.originalPrice ?? 0,
          ).replaceAll(',', '.')}đ`}</TextCell>
        ),
        totalAmount: (
          <TextCell style={{ textAlign: 'right' }}>{`${formatNumber(
            ticket?.totalPrice ?? 0,
          ).replaceAll(',', '.')}đ`}</TextCell>
        ),
      };

      if (isPolicyOrder) {
        rowData.penaltyPolicy = (
          <TextCell
            style={{ textAlign: 'right' }}
          >{`${ticket?.percent}%`}</TextCell>
        );
        rowData.fineAmount = (
          <TextCell style={{ textAlign: 'right' }}>{`${formatNumber(
            ticket?.penaltyFee ?? 0,
          ).replaceAll(',', '.')}đ`}</TextCell>
        );
      }

      return rowData;
    });
  }, [isPolicyOrder, cancellationListData]);

  return (
    <div>
      <Title style={{ textTransform: 'initial', marginBottom: 16 }}>
        {translations?.['b2b.txt.ticketCancellationList'] ?? ''} -{' '}
        {translations?.['b2b.totalTicketAmount']?.replace(
          '${amount}',
          totalTicketAmount.current,
        ) ?? ''}
      </Title>
      <div ref={anchorPoint} />
      <StyledTable
        columns={columns}
        dataSource={dataSource}
        styleSlots={{
          th: {
            fontWeight: 600,
            fontSize: 14,
            textAlign: 'left',
          },
        }}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: onScrollIntoElement,
        }}
      />
    </div>
  );
};

export default memo(CancellationList);
